/**
 * Mixpanel tracking utility
 * 
 * This file handles Mixpanel initialization and provides helper methods for tracking events.
 * It sets up global click tracking and provides utilities for tracking specific interactions.
 */

// Check if Mixpanel is already initialized
let mixpanelInitialized = false;

/**
 * Initialize Mixpanel with the token from meta tag
 * @returns {boolean} Whether initialization was successful
 */
const initMixpanel = () => {
  // Check if we're in development environment - don't initialize Mixpanel in dev
  const isDevelopment = document.querySelector('body')?.classList.contains('development');
  
  if (isDevelopment) {
    return false;
  }
  
  // If already initialized, don't initialize again
  if (mixpanelInitialized || window.mixpanel?.__loaded) {
    // Register super properties for the user if they exist
    registerUserProperties();
    return true;
  }

  // Get the Mixpanel token from the meta tag (will be set server-side)
  const token = document.querySelector('meta[name="mixpanel-token"]')?.content;
  
  if (!token) {
    console.warn("Mixpanel token not found. Tracking will be disabled.");
    return false;
  }

  // Check if Mixpanel is already loaded by the Rails backend
  if (window.mixpanel) {
    mixpanelInitialized = true;
    return true;
  }

  // Load Mixpanel snippet (modified from Mixpanel docs)
  (function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
  for(h=0;h<i.length;h++)g(a,i[h]);b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}})(document,window.mixpanel||[]);

  // Initialize with token
  mixpanel.init(token, {
    track_pageview: false, // We'll handle page views manually
    debug: process.env.NODE_ENV !== "production"
  });
  
  mixpanelInitialized = true;
  
  // Register super properties for the user
  registerUserProperties();
  
  return true;
};

/**
 * Register user properties as super properties in Mixpanel
 * These will be included in all events automatically
 */
const registerUserProperties = () => {
  if (!window.mixpanel) return;
  
  try {
    // Get user ID and admin link from meta tags
    const userId = document.querySelector('meta[name="current-user-id"]')?.content;
    const adminUserLink = document.querySelector('meta[name="admin-user-link"]')?.content;
    
    // Get account info from meta tags
    const accountId = document.querySelector('meta[name="current-account-id"]')?.content;
    const adminAccountLink = document.querySelector('meta[name="admin-account-link"]')?.content;
    
    // Build properties object
    const properties = {};
    
    // Add user properties if available
    if (userId && adminUserLink) {
      properties.user_id = userId;
      properties.admin_user_link = adminUserLink;
    }
    
    // Add account properties if available
    if (accountId && adminAccountLink) {
      properties.account_id = accountId;
      properties.admin_account_link = adminAccountLink;
    }
    
    // Register all properties
    if (Object.keys(properties).length > 0) {
      mixpanel.register(properties);
    }
  } catch (e) {
    console.error("Error registering user properties:", e);
  }
};

/**
 * Track a click event with standardized properties
 * @param {HTMLElement} element - The DOM element that was clicked
 * @param {Object} additionalProps - Additional properties to include in the event
 */
const trackClick = (element, additionalProps = {}) => {
  if (!window.mixpanel || !mixpanelInitialized) return;

  // Track click event

  try {
    // Get element details
    const tagName = element.tagName.toLowerCase();
    const id = element.id || null;
    const classes = Array.from(element.classList).join(" ") || null;
    const text = element.innerText?.trim() || null;
    const href = element.href || element.closest('a')?.href || null;
    
    // Get input-specific details
    let interactionType = "click";
    let elementValue = null;
    let interactionContext = null;
    
    // Handle form elements
    if (tagName === "input" || tagName === "select" || tagName === "textarea") {
      // Set interaction type based on element type
      if (tagName === "input" && element.type === "checkbox") {
        interactionType = "checkbox_toggled";
        elementValue = element.checked;
      } else if (tagName === "input" && element.type === "radio") {
        interactionType = "radio_selected";
        elementValue = element.value;
      } else if (tagName === "select") {
        interactionType = "option_selected";
        elementValue = element.value;
      } else {
        interactionType = `${tagName}_interaction`;
        elementValue = element.value;
      }
      
      // Get label text if available
      const labelText = element.labels?.[0]?.innerText?.trim() || null;
      
      // Add label text to context if available
      if (labelText) {
        interactionContext = interactionContext || {};
        interactionContext.label_text = labelText;
      }
      
      // Get parent row data for table checkboxes
      const tableRow = element.closest('tr');
      if (tableRow) {
        // Get table headers to use as column names
        const table = tableRow.closest('table');
        const headerCells = table ? Array.from(table.querySelectorAll('thead th')) : [];
        const headerTexts = headerCells.map(th => th.innerText?.trim() || null);
        
        const rowCells = Array.from(tableRow.cells || []);
        const rowData = {};
        
        // Add tool name as a specific property for AI tools table
        if (table && table.id === 'ai-tools-table') {
          rowData.table_id = table.id;
          // Tool name is typically in the second column (index 1)
          if (rowCells.length > 1) {
            rowData.tool_name = rowCells[1].innerText?.trim() || null;
            rowData.hours_used = rowCells[2].innerText?.trim() || null;
          }
        }
        
        // Add all cell data with header names when available
        rowCells.forEach((cell, index) => {
          if (index > 0 && cell.innerText?.trim()) { // Skip first cell (checkbox cell)
            const headerText = headerTexts[index];
            const key = headerText ? 
              `${headerText.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${index}` : 
              `column_${index}`;
            rowData[key] = cell.innerText.trim();
          }
        });
        
        interactionContext = rowData;
      }
    }
    
    // Get data attributes from the clicked element and its parents with data-track
    const dataAttributes = {};
    
    // First check the element itself
    for (const key in element.dataset) {
      dataAttributes[`data_${key}`] = element.dataset[key];
    }
    
    // Then look for any parent elements with data-track and merge their attributes
    // This allows adding data-track to a parent container and having it apply to all children
    let parent = element.closest('[data-track]');
    while (parent && parent !== element) {
      for (const key in parent.dataset) {
        // Only add if not already present (child attributes take precedence)
        if (!dataAttributes[`data_${key}`]) {
          dataAttributes[`data_${key}`] = parent.dataset[key];
        }
      }
      // Look for next parent with data-track
      parent = parent.parentElement ? parent.parentElement.closest('[data-track]') : null;
    }
    


    // Get page information
    const pagePath = window.location.pathname;
    const pageTitle = document.title;
    const pageUrl = window.location.href;

    // Get user ID and admin link from meta tags
    const userId = document.querySelector('meta[name="current-user-id"]')?.content;
    const adminUserLink = document.querySelector('meta[name="admin-user-link"]')?.content;
    
    // Get account info from meta tags
    const accountId = document.querySelector('meta[name="current-account-id"]')?.content;
    const adminAccountLink = document.querySelector('meta[name="admin-account-link"]')?.content;
    
    // Combine all properties
    const eventProps = {
      element_type: tagName,
      element_id: id,
      element_classes: classes,
      element_text: text,
      element_href: href,
      interaction_type: interactionType,
      element_value: elementValue,
      interaction_context: interactionContext,
      page_path: pagePath,
      page_title: pageTitle,
      page_url: pageUrl,
      user_id: userId || null,
      admin_user_link: adminUserLink || null,
      account_id: accountId || null,
      admin_account_link: adminAccountLink || null,
      ...dataAttributes,
      ...additionalProps
    };

    // Track the event asynchronously to avoid blocking the UI
    Promise.resolve().then(() => {
      try {
        mixpanel.track("element_clicked", eventProps);
      } catch (e) {
        console.error("Error tracking click event:", e);
      }
    });
  } catch (e) {
    console.error("Error preparing click event data:", e);
  }
};

/**
 * Setup global click tracking on the document
 * Intelligently filters clicks to track only meaningful interactions
 */
const setupClickTracking = () => {
  if (!window.mixpanel || !mixpanelInitialized) return;
  
  // Use a flag to prevent infinite recursion
  let isProcessingClick = false;
  
  // Remove any existing click listeners to prevent duplicates
  document.removeEventListener('click', handleClick);
  
  // Add the click listener with a debounced handler to prevent excessive tracking
  document.addEventListener('click', debounce(handleClick, 300));
  
  function handleClick(event) {
    // Prevent infinite recursion
    if (isProcessingClick) return;
    isProcessingClick = true;
    
    try {
      // Get the clicked element
      const clickedElement = event.target;
      // Process clicked element
      
      // Skip tracking if element or its parent has data-no-track attribute
      if (clickedElement.closest('[data-no-track]')) {
        isProcessingClick = false;
        return;
      }
      
      // Skip tracking for certain elements that are likely not meaningful
      const tagName = clickedElement.tagName.toLowerCase();
      const skipTags = ['html', 'body', 'script', 'style', 'svg', 'path', 'rect', 'circle'];
      if (skipTags.includes(tagName)) {
        isProcessingClick = false;
        return;
      }
      
      // Skip tracking for elements without text, href, id, or meaningful classes
      // unless they have a data-track attribute
      const hasText = clickedElement.innerText?.trim();
      const hasHref = clickedElement.href;
      const hasId = clickedElement.id;
      const hasClasses = clickedElement.classList.length > 0;
      const hasDataTrack = clickedElement.hasAttribute('data-track');
      const hasDataAnalytics = clickedElement.hasAttribute('data-analytics');
      
      // Find the most meaningful element to track (the clicked element or its closest interactive parent)
      const interactiveSelectors = 'a, button, input[type="submit"], input[type="button"], [role="button"], .js-track-click, li[role="tab"], [data-track], [data-analytics]';
      const interactiveParent = clickedElement.closest(interactiveSelectors);
      
      // Track the most meaningful element
      const elementToTrack = interactiveParent || clickedElement;
      
      // Only track if the element has some meaningful properties or is explicitly marked for tracking
      if (hasText || hasHref || hasId || hasClasses || hasDataTrack || hasDataAnalytics || interactiveParent) {
        trackClick(elementToTrack);
      }
    } finally {
      // Always reset the flag
      isProcessingClick = false;
    }
  }
};

// Export the functions
/**
 * Simple debounce function to prevent excessive event firing
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce wait time in milliseconds
 * @returns {Function} - The debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export { initMixpanel, trackClick, setupClickTracking };
